import FormSection from '@/components/Forms/FormSection';
import { intercomTarget } from '@/helpers/intercomTarget';
import usePermissions from '@/hooks/usePermissions';
import { Accordion, Box, Button, TextInput } from '@palmetto/palmetto-components';
import { ChangeEvent, FocusEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import {
  GeneratorEquipmentItem,
  GeneratorEquipmentType,
  GeneratorFuelType,
  GeneratorPreliminaryEquipmentItem,
  GeneratorSystem,
  UserPermissions,
} from 'types';

// Site prep, accessories, and discounts are temporarily commented out for the generator program; may return.
// import { AccessoriesSection } from './AccessoriesSection';
// import { DiscountsSection } from './DiscountsSection';
import { EquipmentAndMaterialsSection } from './EquipmentAndMaterialsSection';
import { InstallPackageEquipmentSection } from './InstallPackageEquipmentSection';
// import { SitePrepSection } from './SitePrepSection';

type GeneratorSystemItemForm = Omit<GeneratorSystem, 'equipment'> & {
  equipment: {
    totalCost?: number;
    items: (GeneratorEquipmentItem | GeneratorPreliminaryEquipmentItem)[];
  };
};

interface SystemPanelProps {
  index: number;
  isOpen: boolean;
  onToggle: (index: number) => void;
  onRemove: (index: number) => void;
  canRemove: boolean;
  isInstallPackage: boolean;
  isPreliminary: boolean;
  isPackageEditable: boolean;
  isDisabled: boolean;
  systemTitleRef: (el: HTMLDivElement | null) => void;
}

const SystemPanel = ({
  index,
  isOpen,
  onToggle,
  onRemove,
  canRemove,
  isInstallPackage,
  isPreliminary,
  isPackageEditable,
  isDisabled,
  systemTitleRef,
}: SystemPanelProps) => {
  const { control, setValue } = useFormContext<any>();
  const systemName = useWatch({ control, name: `systems.${index}.name` });
  const equipmentItems: any[] = useWatch({ control, name: `systems.${index}.equipment.items` });
  const equipmentTotalCost = useWatch({ control, name: `systems.${index}.equipment.totalCost` });
  const equipmentTotalCostName = `systems.${index}.equipment.totalCost`;
  const nameFieldName = `systems.${index}.name`;

  const handleToggle = useCallback(() => onToggle(index), [onToggle, index]);
  const handleRemoveClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const confirmed = window.confirm('Are you sure you want to remove this system?');
      if (!confirmed) return;
      onRemove(index);
    },
    [onRemove, index],
  );

  const handleTotalCostFocus = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      if (event.target.value === '0') {
        setValue(equipmentTotalCostName, '', { shouldDirty: true });
      }
    },
    [setValue, equipmentTotalCostName],
  );

  const handleTotalCostBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>, fieldOnBlur: () => void) => {
      const value = parseFloat(event.target.value);
      setValue(equipmentTotalCostName, isNaN(value) ? 0 : value, { shouldDirty: true });
      fieldOnBlur();
    },
    [setValue, equipmentTotalCostName],
  );

  const handleTotalCostChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      if (value.endsWith('.')) {
        setValue(equipmentTotalCostName, value, { shouldDirty: true });
      } else {
        const numericValue = Number(value);
        if (!isNaN(numericValue)) {
          setValue(equipmentTotalCostName, value, { shouldDirty: true });
        }
      }
    },
    [setValue, equipmentTotalCostName],
  );

  const totalCostDisabled = isDisabled || !equipmentItems?.length;

  return (
    <Accordion.Panel isOpen={isOpen}>
      <Accordion.PanelSummary
        hasCaret="right"
        onToggle={handleToggle}
        isDetailsOpen={isOpen}
        padding={{ desktop: !isOpen ? 'xl 0' : 'xl 0 0 0', base: 'xl lg' }}
      >
        <Box>
          <Box direction="row">
            <Box direction="row" gap="xs" alignItems="center">
              <Box fontSize="xl" fontWeight="medium" ref={systemTitleRef}>
                {systemName}
              </Box>
              {canRemove ? (
                <Button
                  variant="tertiary"
                  tone="neutral"
                  size="sm"
                  isDisabled={!isPackageEditable}
                  onClick={handleRemoveClick}
                  iconPrefix="trash"
                />
              ) : null}
            </Box>
          </Box>
        </Box>
      </Accordion.PanelSummary>
      <FormSection baseStyle={{ display: 'block' }}>
        <Box direction="row" justifyContent="space-between" gap="lg" display="block" id={`system-${index}`}>
          <Box gap="lg">
            <Box
              direction={{ base: 'column', desktop: 'row' }}
              gap="xl"
              padding={{ desktop: 'xl 0', base: '0 lg' }}
              style={{ flexWrap: 'wrap' }}
            >
              <Controller
                control={control}
                name={nameFieldName}
                render={({ field }) => (
                  <TextInput
                    id={nameFieldName}
                    name={nameFieldName}
                    label="Name"
                    value={field.value ?? `System ${index + 1}`}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    isDisabled={isDisabled}
                    width={{ tablet: '4xl' }}
                  />
                )}
              />
            </Box>
            {!isInstallPackage ? (
              <EquipmentAndMaterialsSection
                systemIndex={index}
                isPreliminary={isPreliminary}
                // [PLACEHOLDER] Generator KB link — confirm URL with content team
                helpLink="https://hep.palmetto.finance/en/articles/12003806-hep-residential-system-categories"
                isPackageEditable={isPackageEditable}
              />
            ) : (
              <InstallPackageEquipmentSection systemIndex={index} isPackageEditable={isPackageEditable} />
            )}
            <FormSection title="Equipment Total Cost" baseStyle={{ borderWidth: '0' }}>
              <Box gap="xl" alignSelf="flex-end">
                {!isInstallPackage ? (
                  <Controller
                    control={control}
                    name={equipmentTotalCostName}
                    render={({ field }) => (
                      <TextInput
                        id={equipmentTotalCostName}
                        name={equipmentTotalCostName}
                        label=""
                        hideLabel
                        prefix="$"
                        width="2xl"
                        value={field.value ?? ''}
                        isDisabled={totalCostDisabled}
                        onFocus={handleTotalCostFocus}
                        onBlur={(e) => handleTotalCostBlur(e, field.onBlur)}
                        onChange={handleTotalCostChange}
                      />
                    )}
                  />
                ) : (
                  <Box fontSize="md" fontWeight="medium">
                    ${Number(equipmentTotalCost || 0).toLocaleString()}
                  </Box>
                )}
              </Box>
            </FormSection>
            {/* Site prep, accessories, and discounts are temporarily commented out for the generator program; may return.
            <SitePrepSection
              systemIndex={index}
              isInstallPackage={isInstallPackage}
              isPackageEditable={isPackageEditable}
            />
            <AccessoriesSection
              systemIndex={index}
              isInstallPackage={isInstallPackage}
              isPackageEditable={isPackageEditable}
            />
            <DiscountsSection
              systemIndex={index}
              isInstallPackage={isInstallPackage}
              isPackageEditable={isPackageEditable}
            /> */}
          </Box>
        </Box>
      </FormSection>
    </Accordion.Panel>
  );
};

export const SystemSection = ({
  isInstallPackage = false,
  isPreliminary = false,
  isPackageEditable = true,
}: {
  isInstallPackage?: boolean;
  isPreliminary?: boolean;
  isPackageEditable?: boolean;
}) => {
  const { control } = useFormContext<any>();
  const { fields, append, remove } = useFieldArray({ control, name: 'systems' });
  const permissions = usePermissions();
  const userOnlyHasReaderPermission = permissions?.every((permission) =>
    permission.includes(UserPermissions.reader),
  );
  const isDisabled = !!userOnlyHasReaderPermission || !isPackageEditable;

  const [openPanels, setOpenPanels] = useState<Record<string, boolean>>(() => {
    const initialPanels: Record<string, boolean> = {};
    fields.forEach((_: any, index: number) => {
      initialPanels[(index + 1).toString()] = true;
    });
    return initialPanels;
  });

  const systemTitleRefs = useRef<Record<number, HTMLDivElement | null>>({});

  const handleAddSystem = useCallback(() => {
    const closeExistingPanels = Object.keys(openPanels).reduce((acc, key) => ({ ...acc, [key]: false }), {});
    const newSystemIndex = fields.length;
    setOpenPanels({ ...closeExistingPanels, [`${newSystemIndex + 1}`]: true });
    const newSystem: GeneratorSystemItemForm = {
      name: `System ${newSystemIndex + 1}`,
      equipment: {
        totalCost: 0,
        items: isPreliminary
          ? [
              {
                type: GeneratorEquipmentType.generator,
                quantity: 1,
                fuelType: '' as unknown as GeneratorFuelType,
              },
            ]
          : [],
      },
      // Site prep, accessories, and discounts are temporarily commented out for the generator program; may return.
      // sitePrep: { totalCost: 0, items: [] },
      // accessories: { totalCost: 0, items: [] },
      // discounts: { totalCost: 0, items: [] },
    };
    append(newSystem);

    setTimeout(() => {
      const newSystemTitleRef = systemTitleRefs.current[newSystemIndex];
      if (newSystemTitleRef) {
        newSystemTitleRef.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  }, [openPanels, fields.length, isPreliminary, append]);

  const handleToggle = useCallback((index: number) => {
    const panelIndex = `${index + 1}`;
    setOpenPanels((prev) => ({ ...prev, [panelIndex]: !prev[panelIndex] }));
  }, []);

  const setSystemTitleRef = useCallback((index: number) => {
    return (el: HTMLDivElement | null) => {
      systemTitleRefs.current[index] = el;
    };
  }, []);

  useEffect(() => {
    if (fields.length && isInstallPackage) {
      const initialPanels: Record<string, boolean> = {};
      fields.forEach((_, index: number) => {
        initialPanels[`${index + 1}`] = true;
      });
      setOpenPanels(initialPanels);
    }
  }, [fields, isInstallPackage]);

  return (
    <Box padding={{ base: '0 2xs' }}>
      <Accordion>
        <Box>
          {fields.map((fieldItem, index) => {
            const panelIndex = `${index + 1}`;
            return (
              <SystemPanel
                key={fieldItem.id}
                index={index}
                isOpen={!!openPanels[panelIndex]}
                onToggle={handleToggle}
                onRemove={remove}
                canRemove={fields.length > 1}
                isInstallPackage={isInstallPackage}
                isPreliminary={isPreliminary}
                isPackageEditable={isPackageEditable}
                isDisabled={isDisabled}
                systemTitleRef={setSystemTitleRef(index)}
              />
            );
          })}
          {!isInstallPackage && (
            <Button
              variant="secondary"
              iconPrefix="add"
              style={{ alignSelf: 'flex-end', marginTop: '16px' }}
              isDisabled={!isPackageEditable}
              onClick={handleAddSystem}
              data-intercom-target={intercomTarget('system-design', 'add-system')}
            >
              Add System
            </Button>
          )}
        </Box>
      </Accordion>
    </Box>
  );
};
